import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MessageSquare, X, Send, Sparkles, Loader2, BookOpen, Mail, Check } from "lucide-react";
import { sendChat, emailChatTranscript } from "@/lib/api";
import { toast } from "sonner";

const sessionKey = "fixlr_chat_session";
const newId = () => `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const SITE_HOSTS = ["fixlr.me", window.location.host];

// Minimal markdown: [label](url) and bare URLs become links (fixlr.me/help links stay in-app), then **bold**.
const LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s)]+?)(?=[.,;:!?]?(?:\s|$|\*\*))/g;
const linkify = (text, onNavigate, keyBase = "") => {
  const parts = [];
  let last = 0, m, i = 0;
  while ((m = LINK_RE.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const url = m[2] || m[3], label = m[1] || url.replace(/^https?:\/\//, "");
    let path = null;
    try { const u = new URL(url); if (SITE_HOSTS.includes(u.host)) path = u.pathname + u.search + u.hash; } catch { /* keep external */ }
    const key = `${keyBase}l${i++}`;
    parts.push(path
      ? <Link key={key} to={path} onClick={onNavigate} className="text-mk-lime underline underline-offset-2 hover:text-mk-text" data-testid="chat-inline-link">{label}</Link>
      : <a key={key} href={url} target="_blank" rel="noreferrer" className="text-mk-lime underline underline-offset-2 hover:text-mk-text" data-testid="chat-inline-link">{label}</a>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};
const renderInline = (seg, onNavigate, keyBase) => seg.split(/\*([^*\n]+)\*/g).map((part, j) => (j % 2 ? <em key={`${keyBase}i${j}`}>{linkify(part, onNavigate, `${keyBase}i${j}`)}</em> : linkify(part, onNavigate, `${keyBase}p${j}`)));
const renderMd = (text, onNavigate) => text.split(/\*\*([^*]+)\*\*/g).map((seg, i) => (i % 2 ? <strong key={`b${i}`} className="font-semibold">{linkify(seg, onNavigate, `b${i}`)}</strong> : renderInline(seg, onNavigate, `s${i}`)));

const Sources = ({ sources, onNavigate }) => (
  <div className="pl-10 -mt-1 flex flex-wrap items-center gap-1.5" data-testid="chat-sources">
    <span className="text-[10px] uppercase tracking-wider font-mono text-mk-muted">From the help centre</span>
    {sources.map((s) => (
      <Link key={s.slug} to={`/help/${s.slug}`} onClick={onNavigate} data-testid={`chat-source-${s.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-mk-lime/40 bg-mk-lime/10 px-2.5 py-1 text-[11px] text-mk-lime hover:bg-mk-lime hover:text-mk-bg transition-colors">
        <BookOpen className="h-3 w-3" />{s.title}<span className="text-[9px] opacity-70">· {s.audience}</span>
      </Link>
    ))}
  </div>
);

// "Email me this chat": one-field form → POST /api/chat/{session}/email (transcript + guide links; lands in the admin lead inbox).
const EmailTranscript = ({ sessionId }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("busy");
    try { await emailChatTranscript(sessionId, email.trim()); setState("done"); }
    catch (err) { setState("idle"); toast.error(err?.response?.status === 422 ? "That email address doesn't look right" : "Couldn't send — try again in a moment"); }
  };
  if (state === "done") return <div className="pl-10 inline-flex items-center gap-1.5 text-[11px] text-mk-lime" data-testid="chat-email-done"><Check className="h-3.5 w-3.5" />Sent to {email} — check your inbox.</div>;
  if (!open) return <div className="pl-10"><button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-[11px] text-mk-dim hover:text-mk-lime transition-colors" data-testid="chat-email-open"><Mail className="h-3.5 w-3.5" />Email me this chat</button></div>;
  return (
    <form onSubmit={submit} className="ml-10 flex gap-1.5" data-testid="chat-email-form">
      <input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="flex-1 min-w-0 rounded-full px-3 py-1.5 text-xs bg-mk-surface border border-mk-border text-mk-text placeholder:text-mk-muted focus:outline-none focus:border-mk-lime/60" data-testid="chat-email-input" />
      <button type="submit" disabled={state === "busy" || !email.trim()} className="inline-flex items-center gap-1 rounded-full bg-mk-lime px-3 py-1.5 text-xs font-semibold text-mk-bg disabled:opacity-40" data-testid="chat-email-send">{state === "busy" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3 w-3" />}Send</button>
    </form>
  );
};

const Bubble = ({ role, children, onNavigate }) => (
  <div className={`flex ${role === "user" ? "justify-end" : "justify-start"} gap-2.5`}>
    {role === "assistant" && <span className="grid place-items-center h-7 w-7 rounded-full bg-mk-lime text-mk-bg shrink-0"><Sparkles className="h-3.5 w-3.5" /></span>}
    <div className={`rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed max-w-[85%] whitespace-pre-wrap break-words ${role === "user" ? "bg-mk-blue text-white rounded-tr-sm" : "bg-mk-raised text-mk-text rounded-tl-sm"}`}>{typeof children === "string" && role === "assistant" ? renderMd(children, onNavigate) : children}</div>
  </div>
);

const AIChatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => {
    const existing = localStorage.getItem(sessionKey);
    if (existing) return existing;
    const id = newId();
    localStorage.setItem(sessionKey, id);
    return id;
  });
  const scrollRef = useRef(null);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, open, loading]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: t }]);
    setLoading(true);
    try {
      const res = await sendChat(sessionId, t);
      setMessages((m) => [...m, { role: "assistant", content: res.reply, sources: res.sources || [] }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I had trouble responding. Please try again or email sales@fixlr.io." }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestions = ["What's in the Professional bundle?", "How do I approve a quote in the client portal?", "Can I white-label Fixlr?"];

  return (
    <>
      <button
        type="button"
        data-testid="chat-widget-open-btn"
        onClick={() => setOpen((s) => !s)}
        aria-label={open ? "Close chat" : "Open chat"}
        className={`fixed bottom-6 right-6 z-40 grid place-items-center h-14 w-14 rounded-full transition-[transform,background-color] duration-300 ${open ? "bg-mk-raised text-mk-text rotate-90 border border-mk-strong" : "bg-mk-lime text-mk-bg animate-pulse-glow hover:scale-105"}`}
      >
        {open ? <X className="h-5 w-5" /> : <MessageSquare className="h-5 w-5" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.98 }} transition={{ duration: 0.2 }}
            data-testid="chatbot-panel"
            className="fixed bottom-24 right-6 z-40 w-[min(390px,calc(100vw-2rem))] h-[600px] max-h-[75vh] rounded-3xl glass flex flex-col overflow-hidden shadow-[0_40px_100px_-30px_rgba(0,0,0,0.9)]"
          >
            <div className="px-4 py-3 border-b border-mk-border/70 flex items-center gap-3 bg-mk-bg/40">
              <img src="/fixlr-icon.png" alt="" className="h-9 w-9 rounded-lg" />
              <div className="flex-1">
                <div className="font-display font-semibold text-sm">Fixlr Assistant</div>
                <div className="text-[11px] text-mk-dim flex items-center gap-1.5"><span className="live-dot" /> Online · Claude Sonnet 4.5</div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              <Bubble role="assistant">Hi — I'm the Fixlr Assistant. Ask me about any module, the plan builder, white-labelling or how to start your free trial — or ask "how do I…" and I'll answer from the help centre.</Bubble>
              {messages.map((m, i) => (
                <React.Fragment key={i}>
                  <Bubble role={m.role} onNavigate={() => setOpen(false)}>{m.content}</Bubble>
                  {m.sources?.length > 0 && <Sources sources={m.sources} onNavigate={() => setOpen(false)} />}
                </React.Fragment>
              ))}
              {loading && <div className="flex items-center gap-2 text-xs text-mk-muted pl-10"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…</div>}
              {!loading && messages.some((m) => m.role === "assistant") && <EmailTranscript key={messages.length} sessionId={sessionId} />}
              {messages.length === 0 && !loading && (
                <div className="pt-1 flex flex-wrap gap-2 pl-10">
                  {suggestions.map((s, i) => (
                    <button key={s} type="button" data-testid={`chat-suggestion-${i}`} onClick={() => send(s)} className="text-[11px] px-3 py-1.5 rounded-full border border-mk-border bg-mk-surface text-mk-dim hover:border-mk-lime/60 hover:text-mk-text transition-colors">{s}</button>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-mk-border/70 bg-mk-bg/40 flex gap-2" data-testid="chatbot-form">
              <input
                value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask anything about Fixlr…" data-testid="chatbot-input"
                className="flex-1 rounded-full px-4 py-2.5 text-sm bg-mk-surface border border-mk-border text-mk-text placeholder:text-mk-muted focus:outline-none focus:border-mk-lime/60 focus:ring-2 focus:ring-mk-lime/20"
              />
              <button type="submit" disabled={loading || !input.trim()} data-testid="chatbot-send" className="grid place-items-center h-10 w-10 rounded-full bg-mk-lime text-mk-bg disabled:opacity-40 transition-opacity"><Send className="h-4 w-4" /></button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIChatbot;
